// src/pages/ModeratorCriteriaCategoriesPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
import './ModeratorCriteriaCategoriesPage.css';

export default function ModeratorCriteriaCategoriesPage() {
  const locale = localStorage.getItem('locale') || 'kz';

  const t = useMemo(() => ({
    kz: {
      title: 'Критерий категориялары',
      subtitle: 'Категориялар тізімін басқару',
      add: 'Қосу',
      edit: 'Өңдеу',
      save: 'Сақтау',
      delete: 'Жою',
      search: 'Іздеу...',
      id: 'ID',
      name_kz: 'Қазақша атауы',
      name_ru: 'Орысша атауы',
      action: 'Әрекет',
      addTitle: 'Категория қосу',
      editTitle: 'Категорияны өңдеу',
      required: 'Екі тілдегі атауларды толтырыңыз',
      error: 'Қате орын алды',
      empty: 'Дерек табылмады',
      rowsPerPage: 'Көрсету',
      page: 'Бет',
      sort: 'Сұрыптау',
      sortIdAsc: 'ID ↑',
      sortIdDesc: 'ID ↓',
      sortKzAsc: 'Қаз ↑',
      sortKzDesc: 'Қаз ↓',
      sortRuAsc: 'Рус ↑',
      sortRuDesc: 'Рус ↓',
      confirmDelete: 'Осы категорияны жоюға сенімдісіз бе?',
      deleteNotImplemented: 'Жою API кейін қосылады (endpoint жіберіңіз).'
    },
    ru: {
      title: 'Категории критериев',
      subtitle: 'Управление списком категорий',
      add: 'Добавить',
      edit: 'Редактировать',
      save: 'Сохранить',
      delete: 'Удалить',
      search: 'Поиск...',
      id: 'ID',
      name_kz: 'Название (каз)',
      name_ru: 'Название (рус)',
      action: 'Действие',
      addTitle: 'Добавить категорию',
      editTitle: 'Редактировать категорию',
      required: 'Заполните названия на двух языках',
      error: 'Произошла ошибка',
      empty: 'Ничего не найдено',
      rowsPerPage: 'Показать',
      page: 'Стр.',
      sort: 'Сортировка',
      sortIdAsc: 'ID ↑',
      sortIdDesc: 'ID ↓',
      sortKzAsc: 'Каз ↑',
      sortKzDesc: 'Каз ↓',
      sortRuAsc: 'Рус ↑',
      sortRuDesc: 'Рус ↓',
      confirmDelete: 'Вы уверены, что хотите удалить категорию?',
      deleteNotImplemented: 'API удаления добавим позже (скинь endpoint).'
    }
  })[locale], [locale]);

  // data
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // ui
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState('id');
  const [sortDir, setSortDir] = useState('desc'); // новые сверху
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name_kz: '', name_ru: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [query, sortKey, sortDir, pageSize]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    if (modalOpen) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [modalOpen]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/backoffice/criteria-categories');
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setFormData({ name_kz: '', name_ru: '' });
    setEditingId(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const openEditModal = (c) => {
    setFormData({ name_kz: c?.name_kz || '', name_ru: c?.name_ru || '' });
    setEditingId(c?.id ?? null);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name_kz?.trim() || !formData.name_ru?.trim()) {
      alert(t.required);
      return;
    }

    try {
      if (isEditing) {
        await axios.put(`/backoffice/criteria-categories/${editingId}`, {
          name_kz: formData.name_kz.trim(),
          name_ru: formData.name_ru.trim(),
        });
      } else {
        await axios.post('/backoffice/criteria-categories', {
          name_kz: formData.name_kz.trim(),
          name_ru: formData.name_ru.trim(),
        });
      }

      await fetchCategories();
      setModalOpen(false);
    } catch (e) {
      console.error(e);
      alert(t.error);
    }
  };

  const handleDelete = async () => {
  if (!isEditing || !editingId) return;

  const ok = window.confirm(t.confirmDelete);
  if (!ok) return;

  try {
    await axios.delete(`/backoffice/criteria-categories/${editingId}`);
    await fetchCategories();
    setModalOpen(false);
  } catch (e) {
    console.error(e);
    alert(t.error);
  }
};


  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = categories;

    if (q) {
      list = list.filter((c) => {
        const kz = (c.name_kz || '').toLowerCase();
        const ru = (c.name_ru || '').toLowerCase();
        return kz.includes(q) || ru.includes(q) || String(c.id || '').includes(q);
      });
    }

    return [...list].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;

      if (sortKey === 'id') return (Number(a.id) - Number(b.id)) * dir;

      const av = String(a[sortKey] || '').toLowerCase();
      const bv = String(b[sortKey] || '').toLowerCase();
      return av.localeCompare(bv) * dir;
    });
  }, [categories, query, sortKey, sortDir]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const pageItems = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, safePage, pageSize]);

  const sortOptions = [
    { key: 'id', dir: 'desc', label: t.sortIdDesc },
    { key: 'id', dir: 'asc', label: t.sortIdAsc },
    { key: 'name_kz', dir: 'asc', label: t.sortKzAsc },
    { key: 'name_kz', dir: 'desc', label: t.sortKzDesc },
    { key: 'name_ru', dir: 'asc', label: t.sortRuAsc },
    { key: 'name_ru', dir: 'desc', label: t.sortRuDesc },
  ];

  return (
    <div className="mcc-page">
      <div className="mcc-header">
        <div className="mcc-titleBlock">
          <div className="mcc-titleRow">
            <span className="mcc-mark" />
            <h1 className="mcc-title">{t.title}</h1>
          </div>
          <div className="mcc-subtitle">{t.subtitle}</div>
        </div>

        <button className="mcc-btn mcc-btnPrimary" onClick={openAddModal}>
          <span className="mcc-btnIcon">＋</span>
          {t.add}
        </button>
      </div>

      <div className="mcc-card">
        <div className="mcc-toolbar">
          <div className="mcc-tools">
            <div className="mcc-search">
              <span className="mcc-searchIcon">🔎</span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="mcc-searchInput"
                placeholder={t.search}
              />
            </div>

            <select
              className="mcc-select"
              value={`${sortKey}:${sortDir}`}
              onChange={(e) => {
                const [k, d] = e.target.value.split(':');
                setSortKey(k);
                setSortDir(d);
              }}
              aria-label={t.sort}
            >
              {sortOptions.map((o) => (
                <option key={`${o.key}:${o.dir}`} value={`${o.key}:${o.dir}`}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mcc-tableWrap">
          <table className="mcc-table">
            <thead>
              <tr>
                <th className="mcc-colId">{t.id}</th>
                <th>{t.name_kz}</th>
                <th>{t.name_ru}</th>
                <th className="mcc-colAction">{t.action}</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="mcc-skeletonRow">
                    <td><div className="mcc-skeleton" /></td>
                    <td><div className="mcc-skeleton" /></td>
                    <td><div className="mcc-skeleton" /></td>
                    <td><div className="mcc-skeleton" /></td>
                  </tr>
                ))
              ) : pageItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="mcc-empty">{t.empty}</td>
                </tr>
              ) : (
                pageItems.map((c) => (
                  <tr key={c.id}>
                    <td className="mcc-colId">{c.id}</td>
                    <td className="mcc-tdMain">{c.name_kz}</td>
                    <td className="mcc-tdMain">{c.name_ru}</td>
                    <td className="mcc-colAction">
                      <button className="mcc-btn mcc-btnSoft" onClick={() => openEditModal(c)}>
                        {t.edit}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* mobile cards */}
          <div className="mcc-cards">
            {loading ? (
              [...Array(6)].map((_, i) => (
                <div key={i} className="mcc-cardRow mcc-skeletonCard">
                  <div className="mcc-skeleton" />
                  <div className="mcc-skeleton" />
                  <div className="mcc-skeleton" />
                </div>
              ))
            ) : pageItems.length === 0 ? (
              <div className="mcc-emptyCard">{t.empty}</div>
            ) : (
              pageItems.map((c) => (
                <div key={c.id} className="mcc-cardRow">
                  <div className="mcc-cardTop">
                    <div className="mcc-cardTitle">{c.name_kz}</div>
                    <div className="mcc-badge">ID: {c.id}</div>
                  </div>
                  <div className="mcc-cardMeta">
                    <div><span>Қаз:</span> {c.name_kz}</div>
                    <div><span>Рус:</span> {c.name_ru}</div>
                  </div>
                  <div className="mcc-cardActions">
                    <button className="mcc-btn mcc-btnSoft" onClick={() => openEditModal(c)}>
                      {t.edit}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="mcc-footer">
          <div className="mcc-left">
            <span className="mcc-muted">{t.rowsPerPage}:</span>
            <select
              className="mcc-select mcc-selectSmall"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>

            <span className="mcc-muted mcc-range">
              {total === 0 ? '0' : ((safePage - 1) * pageSize + 1)}–{Math.min(safePage * pageSize, total)} / {total}
            </span>
          </div>

          <div className="mcc-right">
            <button className="mcc-btn mcc-btnGhost" disabled={safePage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</button>
            <div className="mcc-pagePill">{t.page} <b>{safePage}</b> / {totalPages}</div>
            <button className="mcc-btn mcc-btnGhost" disabled={safePage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="mcc-modalOverlay" onClick={() => setModalOpen(false)} role="dialog" aria-modal="true">
          <div className="mcc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mcc-modalHeader">
              <div className="mcc-modalTitle">{isEditing ? t.editTitle : t.addTitle}</div>
              <button className="mcc-iconBtn" onClick={() => setModalOpen(false)} aria-label="close">✕</button>
            </div>

            <div className="mcc-modalBody">
              <div className="mcc-field">
                <label className="mcc-label">{t.name_kz}</label>
                <input
                  className="mcc-input"
                  value={formData.name_kz}
                  onChange={(e) => setFormData(p => ({ ...p, name_kz: e.target.value }))}
                />
              </div>

              <div className="mcc-field">
                <label className="mcc-label">{t.name_ru}</label>
                <input
                  className="mcc-input"
                  value={formData.name_ru}
                  onChange={(e) => setFormData(p => ({ ...p, name_ru: e.target.value }))}
                />
              </div>
            </div>

            <div className="mcc-modalFooter">
              {isEditing && (
                <button className="mcc-btn mcc-btnDanger" onClick={handleDelete}>
                  {t.delete}
                </button>
              )}
              <button className="mcc-btn mcc-btnPrimary" onClick={handleSubmit}>
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
